// Shared parser for hourly rates and hours per week, incl. ranges.
// Pure TS (no Deno/browser APIs): imported by edge functions AND src/lib/structuredData.ts,
// so the JobPosting schema, the page, the prerender and the job feed always agree.
// Recognises e.g. "35", "35,50", "35-40", "35.00-39.63", "35 – 40", "35 tot 40",
// "€35,50 - €40", "1 tot 4", "8-12 uur".

export type NumRange = { min: number; max: number };

function parseRange(raw: unknown, limit: number): NumRange | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "number") return Number.isFinite(raw) && raw > 0 && raw <= limit ? { min: raw, max: raw } : null;
  const s = String(raw).toLowerCase()
    .replace(/€|euro|eur/g, "")
    .replace(/per\s*(uur|week)|p\/[uw]|\/\s*(uur|week)|uur/g, "")
    .replace(/\s+tot\s+|[–—]/g, "-")
    .replace(/\s+/g, "");
  const m = s.match(/^(\d{1,3}(?:[.,]\d{1,2})?)(?:-(\d{1,3}(?:[.,]\d{1,2})?))?$/);
  if (!m) return null;
  let a = Number(m[1].replace(",", "."));
  let b = m[2] ? Number(m[2].replace(",", ".")) : a;
  if (a > b) [a, b] = [b, a];
  if (!(a > 0) || b > limit) return null;
  return { min: a, max: b };
}

export const parseRateRange = (raw: unknown) => parseRange(raw, 500);
export const parseHoursRange = (raw: unknown) => parseRange(raw, 80);

const fmtRate = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2).replace(".", ","));
const fmtHours = (n: number) => String(Math.round(n * 10) / 10).replace(".", ",");

/** "€ 35–39,63 per uur" / "€ 35 per uur", or null. */
export function rateLabel(raw: unknown): string | null {
  const r = parseRateRange(raw);
  if (!r) return null;
  return r.min === r.max ? `€ ${fmtRate(r.min)} per uur` : `€ ${fmtRate(r.min)}–${fmtRate(r.max)} per uur`;
}

/** "1–4 uur per week" / "ca. 8 uur per week", or null. */
export function hoursLabel(raw: unknown): string | null {
  const r = parseHoursRange(raw);
  if (!r) return null;
  return r.min === r.max ? `ca. ${fmtHours(r.min)} uur per week` : `${fmtHours(r.min)}–${fmtHours(r.max)} uur per week`;
}
